// OAuth2 / SSO 接入 API
// 端点:
//   GET  /api/v1/auth/oauth/providers        (公开) 已启用的 SSO provider 列表
//   POST /api/v1/auth/oauth/:provider/login   (公开) 代理登录(proxy),中继到 IdP 后签发 gitman JWT
//   GET  /api/v1/auth/oauth/:provider/login   (公开) 标准授权码流程:302 跳转到 IdP 授权页
//   POST /api/v1/auth/oauth/exchange         (公开) 用一次性 code 换回 access/refresh token
import { apiClient } from './client';
import type { AuthResponse } from './types';

export interface OAuthProviderPublic {
  name: string;
  display_name: string;
  /** 流程类型:'proxy'(代理登录)或 'authorization_code'(标准授权码 + PKCE) */
  flow: string;
  /** 图标 URL(可选),用于登录按钮展示 */
  icon?: string | null;
}

export const oauthApi = {
  /** GET /api/v1/auth/oauth/providers —— 已启用的 provider 列表(登录页按钮用) */
  async listProviders(): Promise<OAuthProviderPublic[]> {
    const { data } = await apiClient.get<OAuthProviderPublic[]>('/v1/auth/oauth/providers');
    return data;
  },

  /**
   * POST /api/v1/auth/oauth/:provider/login —— 代理登录(proxy)。
   * 后端中继到 IdP 的 authorize/confirm/token/userinfo,再用返回的 profile
   * 映射 / 创建本地账号并签发 gitman 自己的 JWT。返回与本地登录完全相同的结构。
   */
  async login(provider: string, username: string, password: string): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>(
      `/v1/auth/oauth/${encodeURIComponent(provider)}/login`,
      { username, password },
    );
    return data;
  },

  /**
   * 标准授权码流程的「发起」(authorization_code)。
   * 浏览器顶层跳转到 gitman 的 `GET /api/v1/auth/oauth/{provider}/login`,
   * 该端点会写 httpOnly PKCE cookie 后 302 跳转到 IdP 授权页,用户在 IdP 完成登录。
   * 注意:必须是顶层导航(window.location),不能走 fetch/axios —— 否则拿不到 Set-Cookie,
   * 且 IdP 的 302 也无法被 SPA 跟随回到 gitman 的 callback。
   */
  redirectToProviderLogin(provider: string): void {
    window.location.assign(`/api/v1/auth/oauth/${encodeURIComponent(provider)}/login`);
  },

  /**
   * POST /api/v1/auth/oauth/exchange —— 标准授权码流程的「兑换」。
   * 前端回调页(`/oauth/callback?code=...`)读取一次性 code,POST 换回真正的
   * access/refresh token(与登录完全相同的结构),存入本地。code 单次有效,用完即焚。
   */
  async exchange(code: string): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/v1/auth/oauth/exchange', { code });
    return data;
  },
};
